import {Trans} from '@lingui/macro';
import _ from 'lodash';
import React, {FC} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {OkCancelDialog} from '../../components/common/dialogs/okCancelDialog';
import {findParentTags, getTagChildrenIds} from '../../helpers/models/tagHelpers';
import {ResolveDispatchProps} from '../../helpers/reduxHelpers';
import {makeMapDispatchToProps, makeMapStateToProps, ResolveStateProps} from '../../helpers/reselectHelpers';
import {TagBulkActionTypesEnum} from '../../models/api/tagBulkActionModel';
import {TagBehaviorsEnum, TagModel} from '../../models/api/tagModel';
import {SettingsTagsUrl} from '../../models/url/settingsUrlModel';
import {AppState} from '../../store/appState';
import {ResourceMap} from '../../store/appTypes';
import {tagsBulkAction} from '../../store/data/tags/tagBulkActions';
import {navigateToLinkAction} from '../../store/stateless/navigationActions';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';

/*
 * Props.
 */

interface SettingsRestoreTagDialogContainerProps {
  isInArchivedView?: boolean;
  searchQuery?: string;
  tagsToActOn: ReadonlyArray<TagModel>;
  searchTagsIdMap: ResourceMap<TagModel>;
  hasAllSelected: boolean;
  tagsUrl: SettingsTagsUrl;
  onRequestClose: Handler;
  onRestore: Handler;
}

/*
 * Selectors.
 */

function getTags(_state: AppState, props: SettingsRestoreTagDialogContainerProps) {
  return props.searchTagsIdMap;
}

function getTagsToActOn(_state: AppState, props: SettingsRestoreTagDialogContainerProps) {
  return props.tagsToActOn;
}

function getIsInSearch(_state: AppState, props: SettingsRestoreTagDialogContainerProps) {
  return Boolean(props.searchQuery);
}
function getIsInArchivedView(_state: AppState, props: SettingsRestoreTagDialogContainerProps) {
  return Boolean(props.isInArchivedView);
}

function makeGetExtraTags() {
  return createSelector([getTagsToActOn, getTags], (tagsToActOn, tags) =>
    _(tagsToActOn)
      .map((t) => findParentTags(tags, t))
      .flatten()
      .value(),
  );
}

function makeGetExtraTagIdsWithSiblings(getExtraTags: ReturnType<typeof makeGetExtraTags>) {
  return createSelector(
    [getExtraTags, getIsInSearch, getIsInArchivedView],
    (extraTags, isInSearch, isInArchivedView) => {
      // If we are not in a search, then we don't need to do anything.
      if (!isInSearch) {
        return extraTags.map((t) => t.id);
      }

      return _(extraTags)
        .map((t) => [t.id, ...getTagChildrenIds(t, isInArchivedView), t.parentTagId])
        .flatten()
        .compact()
        .uniq()
        .value();
    },
  );
}

/*
 * Redux.
 */

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  tagsBulkAction,
  navigateToLink: navigateToLinkAction,
}));

const mapStateToProps = makeMapStateToProps<SettingsRestoreTagDialogContainerProps>()(() => {
  const getExtraTags = makeGetExtraTags();
  return {
    extraTags: getExtraTags,
    tagIdsWithSiblings: makeGetExtraTagIdsWithSiblings(getExtraTags),
  };
});

interface SettingsRestoreTagDialogContainerComponentProps
  extends SettingsRestoreTagDialogContainerProps,
    ResolveDispatchProps<typeof mapDispatchToProps>,
    ResolveStateProps<typeof mapStateToProps> {}

const SettingsRestoreTagDialogContainerComponent: FC<SettingsRestoreTagDialogContainerComponentProps> = (
  props,
) => {
  const {isInArchivedView, searchQuery, hasAllSelected, extraTags, tagIdsWithSiblings} = props;
  const {showBanner, updateBannerOnTagsBulk} = useSettingsBanners();
  const visualTargetTags = _.uniqBy([...props.tagsToActOn, ...extraTags], (t) => t.id);
  const singleTag = visualTargetTags.length === 1 ? visualTargetTags[0] : undefined;
  const targetTagIds = _.uniq([...tagIdsWithSiblings, ...visualTargetTags.map((t) => t.id)]);

  const renderTitle = () => {
    if (targetTagIds.length > 1) {
      return <Trans id="tag-edit-restoreconfirmation-headermany">Restore {targetTagIds.length} tags</Trans>;
    }

    if (!singleTag) {
      return null;
    }

    if (isInArchivedView) {
      return (
        <Trans id="tag-edit-restoreconfirmation-headersinglearchived">
          Restore &quot;{singleTag?.name}&quot;
        </Trans>
      );
    }

    return (
      <Trans id="tag-edit-restoreconfirmation-headersingle">Restore &quot;{singleTag?.name}&quot; tag</Trans>
    );
  };

  const renderCopy = () => {
    if (singleTag) {
      return (
        <Trans id="tag-edit-restoreconfirmation-content-single">
          Restoring this tag will allow it to be added to new conversations.
        </Trans>
      );
    }

    if (targetTagIds.length > 1) {
      return (
        <Trans id="tag-edit-restoreconfirmation-content-many">
          Restoring these tags will:
          <ul>
            <li>Restore the parent tag and all subtags</li>
            <li>Allow all tags to be added to new conversations</li>
          </ul>
        </Trans>
      );
    }

    return null;
  };

  const restoreSingleTag = () => {
    if (!singleTag) {
      return undefined;
    }

    return props.tagsBulkAction({
      type: TagBulkActionTypesEnum.RESTORE,
      namespace: props.tagsUrl.namespace,
      selectedTagIds: [singleTag.id],
    });
  };

  const restoreBulkTags = () => {
    // If we have everything selected, we only pass the search query as the backend knows we want to do a "true select all"
    if (hasAllSelected) {
      return props.tagsBulkAction({
        type: TagBulkActionTypesEnum.RESTORE,
        namespace: props.tagsUrl.namespace,
        searchTerm: searchQuery || '',
        searchOptions: {
          parentTagId: props.tagsUrl.tagId,
          behaviors: [TagBehaviorsEnum.CAN_UPDATE],
          excludeTagIds: _.compact([props.tagsUrl.tagId]),
          isArchived: Boolean(isInArchivedView),
        },
      });
    }

    return props.tagsBulkAction({
      type: TagBulkActionTypesEnum.RESTORE,
      namespace: props.tagsUrl.namespace,
      selectedTagIds: targetTagIds,
    });
  };

  const onOkArchiveClick = async () => {
    if (singleTag) {
      await restoreSingleTag();
    } else {
      await restoreBulkTags();
    }

    showBanner({
      message: singleTag ? (
        <Trans id="settingstags-restoreloading-single">Restoring &quot;{singleTag.name}&quot;</Trans>
      ) : (
        <Trans id="settingstags-restoreloading-many">Restoring {targetTagIds.length} tags</Trans>
      ),
      type: SettingsBannersEnum.LOADING,
    });
    updateBannerOnTagsBulk({
      message: singleTag ? (
        <Trans id="settingstags-restoresuccess-single">Restored &quot;{singleTag.name}&quot;</Trans>
      ) : (
        <Trans id="settingstags-restoresuccess-many">Restored {targetTagIds.length} tags</Trans>
      ),
      type: SettingsBannersEnum.SUCCESS,
    });

    if (props.onRestore) {
      props.onRestore();
    }
  };

  return (
    <OkCancelDialog
      title={renderTitle()}
      okButtonLabel={<Trans id="tag-edit-restore">Restore</Trans>}
      okButtonVariant={ButtonVariantsEnum.PRIMARY}
      onRequestClose={props.onRequestClose}
      onOkClick={onOkArchiveClick}
    >
      {renderCopy()}
    </OkCancelDialog>
  );
};

export const SettingsRestoreTagDialogContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(SettingsRestoreTagDialogContainerComponent);
